import React from 'react'
import {
  ComponentConfig,
  ComponentData,
  Slot,
  SlotComponent,
  usePuck,
} from '@puckeditor/core'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import { UploadedFile } from 'breaktime-theme/src/puck/fields/FileUploadField'
import {
  BorderOverlayProps,
  BorderStyleProps,
  ShadowProps,
  TypographyProps,
  FontWeight,
  borderOverlayField,
  borderOverlayStyle,
  borderStyleField,
  shadowStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  fontWeightOptions,
  applyBorderStyle,
  applyShadowStyle,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import {
  cleanRichtext,
  richtextField,
} from '../ReferralCard/ReferralCard'

/** Components that may NOT be dropped inside PQ slots (step containers +
 * step-internal blocks of other trees). */
export const PQ_DISALLOW = [
  'PQContainer',
  'PQQuestion',
  'PQAnswer',
  'PQResult',
  'ReferralContainer',
  'ReferralCard',
  'ReferralButton',
  'ListicleContainer',
  'ListicleItem',
  'ListicleVoting',
  'PollContainer',
  'PollQuestion',
  'PollAnswer',
  'PollProduct',
  'PollProductPanel',
  'PollNextQuestionButton',
]

export type PQResultLayout =
  | 'ONE_COLUMN'
  | 'TWO_COLUMN'
  | 'ONE_COLUMN_2024'
  | 'TWO_COLUMN_2024'

export type PQResultProps = {
  id?: string
  title?: string
  desc?: string
  resultLayout: PQResultLayout
  /** Step background shown when this result is revealed. */
  backgroundColor?: string
  backgroundImage?: UploadedFile | string
  mobileBackgroundImage?: UploadedFile | string
  /** The result card itself (the "white card"). */
  cardBackground?: string
  cardTextColor?: string
  cardBorder?: BorderStyleProps
  cardShadow?: ShadowProps
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  titleFontFamily?: TypographyProps['fontFamily']
  titleFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  titleFontSize?: string
  titleFontWeight?: FontWeight
  /** flex-direction of the 2024 result content column (e.g. column-reverse
   * to show actions above the title, olipop pattern). */
  contentDirection?: string
  /** CSS order per content region (2024 layouts; flex children). */
  titleOrder?: number
  descOrder?: number
  buttonsOrder?: number
  mediaOrder?: number
  contentOrder?: number
  /** Margin of the buttons wrapper (e.g. `0` to remove the default gap). */
  buttonsMargin?: string
  descFontSize?: string
  borderOverlay?: BorderOverlayProps
  /** Result media (image/video) — legacy `result.image` + `result.videoId`. */
  media: Slot
  /** Result actions: ShopButton (the legacy shop link, fully styleable) and
   * ReferralDropdown blocks. */
  buttons: Slot
  /** Free slot for anything else inside the result card. */
  content: Slot
}

export const PQ_RESULT_DEFAULT_PROPS: PQResultProps = {
  title: 'Result title',
  desc: 'Result description',
  // matches what EMPTY resultLayout renders for legacy beaker results
  // (ResultSlide3 defaults to the classic one-column slide)
  resultLayout: 'ONE_COLUMN',
  media: [],
  buttons: [],
  content: [],
}

/** Plain-text label for a result (used by the answer→result picker). */
export const pqResultLabel = (
  props: { title?: string } | undefined,
  index: number,
): string => {
  const text = (props?.title ?? '').replace(/<[^>]*>/g, '').trim()
  return text || `Result ${index + 1}`
}

/** Editor-only identity colors: each result gets a stable vibrant color by
 * position (visible on light AND sponsored dark backgrounds); chips tint it
 * with alpha for their background. */
export const PQ_RESULT_COLORS = [
  '#e6194b',
  '#3cb44b',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#0aa8a8',
  '#f032e6',
  '#9a6324',
] as const

export const pqResultColor = (index: number): string =>
  PQ_RESULT_COLORS[
    ((index % PQ_RESULT_COLORS.length) + PQ_RESULT_COLORS.length) %
      PQ_RESULT_COLORS.length
  ]

/** Inline style for the quiz "white card" (question box / result inner box):
 * background, text color, typography, border, shadow. Shared by editor
 * renders and the production mapping (`puckCardStyle`). Empty values keep
 * the legacy defaults. Border radius clips content (header images). */
export const pqCardStyle = (p: {
  cardBackground?: string
  cardTextColor?: string
  cardBorder?: BorderStyleProps
  cardShadow?: ShadowProps
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  borderOverlay?: BorderOverlayProps
}): React.CSSProperties => ({
  // the border overlay child positions against the card
  ...(borderOverlayStyle(p.borderOverlay) ? { position: 'relative' } : {}),
  ...(p.cardBackground ? { backgroundColor: p.cardBackground } : {}),
  ...(p.cardTextColor ? { color: p.cardTextColor } : {}),
  ...applyTypography({
    fontFamily: p.fontFamily,
    fontFamilyFallback: p.fontFamilyFallback,
  }),
  ...applyBorderStyle(p.cardBorder ?? {}),
  ...applyShadowStyle(p.cardShadow),
  ...(p.cardBorder?.borderRadius ? { overflow: 'hidden' } : {}),
})

/** Inline style for the result title (size/weight on top of the card font). */
export const pqTitleStyle = (p: {
  titleFontFamily?: TypographyProps['fontFamily']
  titleFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  titleFontSize?: string
  titleFontWeight?: FontWeight
}): React.CSSProperties => ({
  ...applyTypography({
    fontFamily: p.titleFontFamily,
    fontFamilyFallback: p.titleFontFamilyFallback,
  }),
  ...(p.titleFontSize ? { fontSize: p.titleFontSize } : {}),
  ...(p.titleFontWeight
    ? { fontWeight: Number(p.titleFontWeight) || undefined }
    : {}),
})

/** Centered placeholder INSIDE an empty slot (editor-only). */
export const SlotHint = ({
  hint,
  isEmpty,
  children,
}: React.PropsWithChildren<{ hint: string; isEmpty: boolean }>) => (
  <div style={{ position: 'relative' }}>
    {isEmpty && (
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          color: 'rgba(0,0,0,0.35)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {hint}
      </span>
    )}
    {children}
  </div>
)


/**
 * One personality-quiz result. Editor renders the legacy result-slide
 * markup (quiz3.scss / resultSlideNewLayout.scss, imported by PQContainer);
 * production injects these props into the extended legacy ResultSlide.
 */
export const PuckPQResult: ComponentConfig<{
  props: PQResultProps
  fields: ThemeCustomFields
}> = {
  label: 'Personality Quiz Result',
  defaultProps: PQ_RESULT_DEFAULT_PROPS,
  fields: {
    title: richtextField('Title'),
    desc: richtextField('Description'),
    // same options + labels as beaker1's per-result "Result Layout" selector
    resultLayout: {
      type: 'select',
      label: 'Result Layout',
      options: [
        { label: '1 Column', value: 'ONE_COLUMN_2024' },
        { label: '2 Column', value: 'TWO_COLUMN_2024' },
        { label: '1 Column (legacy)', value: 'ONE_COLUMN' },
        { label: '2 Column (legacy)', value: 'TWO_COLUMN' },
      ],
    },
    backgroundColor: {
      type: 'colorPicker',
      label: 'Step Background (on reveal)',
      clearable: true,
    },
    backgroundImage: {
      type: 'fileUpload',
      label: 'Step Background Image (on reveal)',
    },
    mobileBackgroundImage: {
      type: 'fileUpload',
      label: 'Step Background Image (on reveal, mobile)',
    },
    cardBackground: {
      type: 'colorPicker',
      label: 'Card Background',
      clearable: true,
      alpha: true,
    },
    cardTextColor: {
      type: 'colorPicker',
      label: 'Card Text Color',
      clearable: true,
    },
    cardBorder: { ...borderStyleField, label: 'Card Border' },
    cardShadow: { ...shadowStyleField, label: 'Card Shadow' },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    titleFontFamily: { ...fontFamilyField, label: 'Title Font Family' },
    titleFontFamilyFallback: {
      ...fontFamilyFallbackField,
      label: 'Title Font Fallback',
    },
    titleFontSize: {
      type: 'text',
      label: 'Title Font Size',
      placeholder: '40px',
    },
    titleFontWeight: {
      type: 'select',
      label: 'Title Font Weight',
      options: fontWeightOptions,
    },
    contentDirection: {
      type: 'select',
      label: 'Content Direction (2024 layouts)',
      options: [
        { label: 'Default', value: '' },
        { label: 'Column', value: 'column' },
        { label: 'Column Reverse', value: 'column-reverse' },
      ],
    },
    titleOrder: { type: 'number', label: 'Title Order (2024 layouts)' },
    descOrder: { type: 'number', label: 'Description Order' },
    buttonsOrder: { type: 'number', label: 'Buttons Order' },
    mediaOrder: { type: 'number', label: 'Media Order (mobile block)' },
    contentOrder: { type: 'number', label: 'Extra Content Order' },
    buttonsMargin: {
      type: 'text',
      label: 'Buttons Margin (e.g. 0)',
      placeholder: '0',
    },
    descFontSize: {
      type: 'text',
      label: 'Description Font Size',
      placeholder: '20px',
    },
    borderOverlay: borderOverlayField,
    media: { type: 'slot', label: 'Media', disallow: PQ_DISALLOW },
    buttons: {
      type: 'slot',
      label: 'Buttons',
      allow: ['ShopButton', 'ReferralDropdown', 'Button'],
    },
    content: {
      type: 'slot',
      label: 'Extra Content',
      disallow: PQ_DISALLOW,
    },
  },
  render: props => <PQResultRender {...props} />,
}

type PQResultRenderProps = Omit<
  PQResultProps,
  'media' | 'buttons' | 'content'
> & {
  media: SlotComponent
  buttons: SlotComponent
  content: SlotComponent
}

/** Editor-only render — the legacy quiz-end slide markup, stacked. */
function PQResultRender(props: PQResultRenderProps & { id?: string }) {
  const {
    id,
    title,
    desc,
    media: Media,
    buttons: Buttons,
    content: Content,
  } = props
  const titleHtml = cleanRichtext(title)
  const descHtml = cleanRichtext(desc)

  // Identity color by position among the container's results (matches the
  // chips shown on linked answers).
  const puck = usePuck()
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'PQContainer')
  const results = ((container?.props as { results?: ComponentData[] })
    ?.results ?? []) as ComponentData[]
  const index = results.findIndex(r => r.props?.id === id)
  const color = pqResultColor(index >= 0 ? index : 0)
  const stored = index >= 0 ? results[index] : undefined
  const isSlotEmpty = (name: 'media' | 'buttons' | 'content') =>
    !((stored?.props as Record<string, ComponentData[] | undefined>)?.[name]
      ?.length)

  const eyebrow = (
    <span className="answer-eyebrow">
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          marginRight: 6,
        }}
      />
      Results
    </span>
  )

  const isModernLayout = ['ONE_COLUMN_2024', 'TWO_COLUMN_2024'].includes(
    props.resultLayout ?? '',
  )

  // modern layouts preview with the production result_slide markup so the
  // 1-column (stacked) vs 2-column (side by side) difference is visible
  if (isModernLayout) {
    const orientation =
      props.resultLayout === 'TWO_COLUMN_2024' ? 'two-column' : 'one-column'
    return (
      <div
        className="quiz__item bg-img quiz-end with-shadow"
        style={{ borderLeft: `5px solid ${color}`, width: '100%' }}
        data-pq-result-id={id}
      >
        {eyebrow}
        <div className="result_slide_container" style={{ width: '100%' }}>
          <div
            className={`result_slide ${orientation}`}
            style={{ position: 'relative', ...pqCardStyle(props) }}
          >
            {borderOverlayStyle(props.borderOverlay) && (
              <div aria-hidden style={borderOverlayStyle(props.borderOverlay)} />
            )}
            <div className="result_slide_image_wrapper">
              <SlotHint
                hint="Result media (optional) — drop an Image / Video here"
                isEmpty={isSlotEmpty('media')}
              >
                <Media disallow={PQ_DISALLOW} minEmptyHeight={120} />
              </SlotHint>
            </div>
            <div className="result_slide_content">
              <div className="button-wrapper" style={{ width: '100%' }}>
                <SlotHint
                  hint="Buttons (optional) — drop a Shop Button, Button or Referral Dropdown"
                  isEmpty={isSlotEmpty('buttons')}
                >
                  <Buttons
                    allow={['ShopButton', 'ReferralDropdown', 'Button']}
                    minEmptyHeight={48}
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}
                  />
                </SlotHint>
              </div>
              {titleHtml && (
                <h2
                  className="result_slide_content_title"
                  style={pqTitleStyle(props)}
                  dangerouslySetInnerHTML={{ __html: titleHtml }}
                />
              )}
              {descHtml && (
                <div className="description">
                  <div
                    dangerouslySetInnerHTML={{ __html: descHtml }}
                    style={
                      props.descFontSize
                        ? { fontSize: props.descFontSize }
                        : undefined
                    }
                  />
                </div>
              )}
              <div className="social-share">
                <SlotHint
                  hint="Extra content (optional) — drop Social Share or any component"
                  isEmpty={isSlotEmpty('content')}
                >
                  <Content disallow={PQ_DISALLOW} minEmptyHeight={48} />
                </SlotHint>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="quiz__item bg-img quiz-end with-shadow"
      style={{ borderLeft: `5px solid ${color}`, width: '100%' }}
      data-pq-result-id={id}
    >
      <div
        className="quiz__inner-box quiz__result-box quiz__answer no-bg-image orientation-ONE_COLUMN"
        style={pqCardStyle(props)}
      >
        {borderOverlayStyle(props.borderOverlay) && (
          <div aria-hidden style={borderOverlayStyle(props.borderOverlay)} />
        )}
        <div className="quiz__answer-title">
          {eyebrow}
          {titleHtml && (
            <h2
              style={pqTitleStyle(props)}
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />
          )}
        </div>
        <div className="quiz__answer-copy quiz-answer-copy">
          {descHtml && (
            <div
              dangerouslySetInnerHTML={{ __html: descHtml }}
              style={{
                marginBottom: '18px',
                ...(props.descFontSize ? { fontSize: props.descFontSize } : {}),
              }}
            />
          )}
          <SlotHint
            hint="Buttons (optional) — drop a Shop Button, Button or Referral Dropdown"
            isEmpty={isSlotEmpty('buttons')}
          >
            <Buttons
              allow={['ShopButton', 'ReferralDropdown', 'Button']}
              minEmptyHeight={48}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}
            />
          </SlotHint>
          <SlotHint
            hint="Extra content (optional) — drop Social Share or any component"
            isEmpty={isSlotEmpty('content')}
          >
            <Content disallow={PQ_DISALLOW} minEmptyHeight={48} />
          </SlotHint>
        </div>
        <div className="quiz__answer-image-container">
          <SlotHint
            hint="Result media (optional) — drop an Image / Video here"
            isEmpty={isSlotEmpty('media')}
          >
            <Media disallow={PQ_DISALLOW} minEmptyHeight={60} />
          </SlotHint>
        </div>
      </div>
    </div>
  )
}
